export interface ToolUsage {
  favorites: string[];
  recent: string[];
  counts: Record<string, number>;
}

const storageKey = 'wyan.toolbox.usage.v1';

export function normalizeToolUsage(value: unknown): ToolUsage {
  if (!value || typeof value !== 'object') return { favorites: [], recent: [], counts: {} };
  const input = value as Partial<ToolUsage>;
  return {
    favorites: uniqueStrings(input.favorites),
    recent: uniqueStrings(input.recent).slice(0, 6),
    counts: normalizeCounts(input.counts),
  };
}

export function withRecent(usage: ToolUsage, toolId: string): ToolUsage {
  return { ...usage, recent: [toolId, ...usage.recent.filter((id) => id !== toolId)].slice(0, 6) };
}

export function withToolVisit(usage: ToolUsage, toolId: string): ToolUsage {
  const recent = [toolId, ...usage.recent.filter((id) => id !== toolId)].slice(0, 6);
  const current = usage.counts[toolId] || 0;
  return { ...usage, recent, counts: { ...usage.counts, [toolId]: Math.min(current + 1, 9999) } };
}

export function withFavorite(usage: ToolUsage, toolId: string): ToolUsage {
  const favorites = usage.favorites.includes(toolId)
    ? usage.favorites.filter((id) => id !== toolId)
    : [...usage.favorites, toolId];
  return { ...usage, favorites };
}

export function loadToolUsage(): ToolUsage {
  try {
    return normalizeToolUsage(JSON.parse(window.localStorage.getItem(storageKey) || 'null'));
  } catch {
    return { favorites: [], recent: [], counts: {} };
  }
}

export function saveToolUsage(usage: ToolUsage): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(normalizeToolUsage(usage)));
  } catch {
    // Storage can be unavailable in private browsing. Tool execution still works.
  }
}

function uniqueStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((item): item is string => typeof item === 'string' && item.length > 0))];
}

function normalizeCounts(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value)
    .filter(([key, count]) => key.length > 0 && Number.isInteger(count) && Number(count) > 0)
    .map(([key, count]) => [key, Math.min(Number(count), 9999)]));
}
